import {HttpClient, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import DevExpress from 'devextreme';
import CustomStore = DevExpress.data.CustomStore;
import DataSource from 'devextreme/data/data_source';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, retry} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {AppState} from '../../../stores/app.state';
import {Application, Registration, RegistrationRequest, RegistrationResponse, Role} from '../../models/fusion-auth';
import {HttpRequestBuilder, SECURED_STORE} from '../../utils';
import {AppInfoService} from '../general';
import {FusionAuthService} from './fusion-auth.service';

@Injectable({
  providedIn: 'root',
})
export class FusionAuthManagementService {
  constructor(
    private httpClient: HttpClient,
    private appInfo: AppInfoService,
    private fusionAuthService: FusionAuthService,
    private store: Store<AppState>,
    private router: Router
  ) {
  }

  public getFusionAuthApplication(): Observable<Application> {
    return this.httpClient.get<Application>(`${environment.portalBackend.apiUrl}/system/application`);
  }

  public getApplicationRoles(): Observable<Role[]> {
    // @ts-ignore
    return this.getFusionAuthApplication().pipe(retry(2), map((application: Application) => application.roles));
  }

  public getUsers(clientIds: number[] = [], dataSource: BehaviorSubject<DataSource>): CustomStore {
    let url = clientIds.length > 0 ? `/system/users?` : `/system/users`;
    const clientIdsArray: string[] = [];
    clientIds.map(x => {
      clientIdsArray.push(`clientIds=${x}`);
    });
    url += clientIdsArray.join('&');

    return SECURED_STORE({
      routerService: this.router,
      dataSource,
      tokenRefresh: this.fusionAuthService.exchangeRefreshTokenForJWT(),
      loadUrl: `${environment.portalBackend.apiUrl}${url}`,
      loadParams: undefined,
      onLoaded: (users) => {
        this.setRoleToUsers(users);
      },
    });
  }

  public existUser(email: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`${environment.portalBackend.apiUrl}/system/users/exist-user?email=${email}`);
  }

  private setRoleToUsers(users: any[]) {
    users.forEach(user => {
      let role: string = '';
      if (user.registrations.find((r: Registration) => r.applicationId === environment.fusionAuth.applicationId).roles.length > 0) {
        role = user.registrations.find((r: Registration) => r.applicationId === environment.fusionAuth.applicationId).roles[0];
      }
      user.role = role;
    });
  }

  public deleteUser(userId: string): Observable<any> {
    return this.httpClient.delete(`${environment.portalBackend.apiUrl}/system/users/${userId}`, {
      reportProgress: true,
      observe: 'events',
    });
  }

  public createUser(registrationRequest: RegistrationRequest): Observable<HttpResponse<RegistrationResponse>> {
    return this.start()
      .addUriSegment('system/users')
      .setJsonBody(registrationRequest)
      .setMethod('POST')
      .build() as Observable<HttpResponse<RegistrationResponse>>;
  }

  public updateUser(userId: string, registrationRequest: RegistrationRequest): Observable<HttpResponse<RegistrationResponse>> {
    return this.start()
      .addUriSegment(`system/users/${userId}`)
      .setJsonBody(registrationRequest)
      .setMethod('PUT')
      .build() as Observable<HttpResponse<RegistrationResponse>>;
  }

  private start() {
    const httpRequestBuilder = new HttpRequestBuilder(this.httpClient).setUrl(environment.portalBackend.apiUrl);
    httpRequestBuilder.getOptions().reportProgress = false;
    return httpRequestBuilder;
  }

  public createPatientUser(patientId: string, registrationRequest: RegistrationRequest): Observable<HttpResponse<RegistrationResponse>> {
    return this.httpClient.post<any>(
      `${environment.portalBackend.apiUrl}/system/anonymous-users/sign-up/${patientId}`,
      registrationRequest,
      {
        observe: 'response'
      });
  }
}
